<template>
  <form class="vehicle-lookup-form" @submit.prevent="onLookup">
    <p>All fields are required.</p>

    <!-- Plate Number -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isPlateValid }">
      <label :for="plateField">Plate number</label>
      <input class="plate" type="text" maxlength="7" :id="plateField" :aria-describedby="`${plateField}Desc`"
             v-model.lazy.trim="myPlateNumber" aria-required="true" />
      <p v-if="!isPlateValid" class="app-error-message">{{ plateError }}</p>
      <p :id="`${plateField}Desc`" class="sr-only">{{ getFieldDescription(`${plateField}`) }}</p>
    </div>

    <!-- Registration Type -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isTypeValid }">
      <label :for="typeField">Registration type</label>
      <select class="app-custom-select" :id="typeField" :aria-describedby="`${typeField}Desc`"
              v-model.lazy="myRegistrationType" aria-required="true">
        <option :value="{ code: 'none' }">Select a type</option>
        <option v-for="type in registrationTypes" :value="{ code: type.code, description: type.description }" :key="type.code">
          {{ type.description }}
        </option>
      </select>
      <p v-if="!isTypeValid" class="app-error-message">{{ typeError }}</p>
      <p :id="`${typeField}Desc`" class="sr-only">{{ getFieldDescription(`${typeField}`) }}</p>
    </div>

    <!-- Last 6 VIN Digits -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isVinValid }">
      <label :for="vinField">Last six digits of VIN</label>
      <input class="vin" type="text" maxlength="6" :id="vinField" :aria-describedby="`${vinField}Desc`"
             v-model.lazy.trim="myLastVin" aria-required="true" />
      <p v-if="!isVinValid" class="app-error-message">{{ vinError }}</p>
      <p :id="`${vinField}Desc`" class="sr-only">{{ getFieldDescription(`${vinField}`) }}</p>
    </div>

    <!-- Certified Owner -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': (isCertified !== undefined && !isCertified) }">
      <div class="app-checker-group" style="padding-top: 0.75em;">
        <input type="checkbox" :id="certifyField" :aria-describedby="`${certifyField}Desc`"
               @click="myCertifyCheck = !myCertifyCheck" aria-required="true">
        <label :for="certifyField">
          I certify that I am the registered owner of the vehicle identified by the information above,
          or I am authorized by the registered owner to check the registration status.
        </label>
      </div>
      <p v-if="!isCertified" class="app-error-message">{{ certifyError }}</p>
      <p :id="`${certifyField}Desc`" class="sr-only">{{ getFieldDescription(`${certifyField}`) }}</p>
    </div>

    <BaseButtonGroup>
      <template #default>
        <button class="app-btn-primary">
          Look up
        </button>
      </template>
    </BaseButtonGroup>
  </form>
</template>


<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import * as COMMONS from '../../../store/_commons';
import vu from '@/utils/validationUtils'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';

const store = useStore();

// labels
const plateField = 'plateNumber';
const typeField = 'registrationType';
const vinField = 'vinNumberLastSix';
const certifyField = 'certified';

// input fields
const inputPattern = /^[a-zA-Z0-9]*$/;
const myPlateNumber = ref('');
const myRegistrationType = ref({ code: 'none' });
const myLastVin = ref('');
const myCertifyCheck = ref(false);

// field validity
const isPlateValid = ref(true);
const isTypeValid = ref(true);
const isVinValid = ref(true);
const isCertified = ref(undefined);

// --- Computed Properties
const lookupInfo = computed(() => store.getters[COMMONS.VEHICLE_LOOKUP_INFO]);

const plateError = computed(() => {
  return isPlateValid.value ? '' : getFieldErrorMessage(plateField);
});

const typeError = computed(() => {
  return isTypeValid.value ? '' : getFieldErrorMessage(typeField);
});

const vinError = computed(() => {
  return isVinValid.value ? '' : getFieldErrorMessage(vinField);
});

const certifyError = computed(() => {
  return isCertified.value === false ? getFieldErrorMessage(certifyField) : '';
});

/**
 * Error codes and messages mapped from BA's spreadsheet.
 * Used for generating form validation messages and 508-compliant field descriptions.
 *  - Validation (error) messages are per _code_ based on user input. A field can have more than one codes.
 *  - Accessible field description is per _field_.
 * @returns {({ref: string, message: string}|...)[]}
 */
const validationMessages = computed(() => {
  return [
    {
      ref: plateField,
      message: 'Plate Number is required. Acceptable characters are letters or numbers. Please do not include dashes or spaces.',
    },
    { ref: typeField, message: 'Registration type is required.' },
    {
      ref: vinField,
      message: 'Last six digits of VIN are required, you must enter all six digits. Acceptable characters are letters or numbers.',
    },
    {
      ref: certifyField,
      message: 'Please attest that you are the registered owner and are authorized to check the registration status.',
    },
    {
      ref: 'registrationNotFound',
      message: 'No registration was found matching the information provided. Please make sure you are providing information exactly as it appears on the registration.',
    },
  ];
});

const registrationTypes = computed(() => lookupInfo.value.allRegistrationTypes);

// --- Methods ---
const getTypeList = () => store.dispatch(COMMONS.VEHICLE_GET_TYPE_LIST);
const setData = (payload) => store.dispatch(COMMONS.VEHICLE_SET_DATA, payload);

// --- Lifecycle Hook ---
// Call the action to get the list on creation
getTypeList();

const getFieldErrorMessage = (ref, code = undefined) => {
  return vu.getFieldValidationMessage(validationMessages.value, ref, code);
};

const getFieldDescription = (ref) => {
  return vu.getFieldAccessibleDescription(validationMessages.value, ref);
};

const validatePlate = () => {
  const val = myPlateNumber.value;
  isPlateValid.value = ((val.length > 0 && val.length <= 7) && inputPattern.test(val));
};

const validateType = () => {
  isTypeValid.value = (myRegistrationType.value.code !== 'none');
};

const validateVin = () => {
  const val = myLastVin.value;
  isVinValid.value = (val.length === 6 && inputPattern.test(val));
};

const validateCertifyCheck = () => {
  isCertified.value = (myCertifyCheck.value === undefined ? false : myCertifyCheck.value);
};

const validate = () => {
  validatePlate();
  validateType();
  validateVin();
  validateCertifyCheck();
};

const onLookup = () => {
  validate();

  if (isPlateValid.value && isTypeValid.value && isVinValid.value && isCertified.value) {
    setData({
      plateNumber: myPlateNumber.value.toUpperCase(),
      registrationType: myRegistrationType.value,
      vinNumberLastSix: myLastVin.value.toUpperCase(),
      isVehicleOwnerConsented: isCertified.value,
      runLookup: true,
    });
  }
};
</script>

<style lang="scss" scoped>
.vehicle-lookup-form {
  margin-top: 1em;
  input[type="text"] {
    text-transform: uppercase;
  }
  .plate {
    width: 145px;
  }
  .vin {
    width: 125px;
  }
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>
